import { useState } from 'react';

const Theater = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const theaterPrograms = [
    {
      title: 'Season XVIII',
      description: 'Our current season featuring classical and contemporary works',
      image: '/api/placeholder/400/300',
      link: '#season'
    },
    {
      title: 'Company Performances',
      description: 'Professional performances by our talented company dancers',
      image: '/api/placeholder/400/300',
      link: '#performances'
    },
    {
      title: 'Community Outreach',
      description: 'Bringing ballet to schools and community centers',
      image: '/api/placeholder/400/300',
      link: '#outreach'
    }
  ];

  const upcomingShows = [
    {
      title: 'The Nutcracker',
      date: 'December 15-17, 2024',
      venue: 'Mount Baker Theatre',
      description: 'Our annual holiday tradition featuring local dancers and guest artists.',
      ticketLink: '#tickets'
    },
    {
      title: 'Spring Gala',
      date: 'March 22, 2025',
      venue: 'Bellingham High School',
      description: 'An evening of diverse choreography showcasing our company dancers.',
      ticketLink: '#tickets'
    },
    {
      title: 'Contemporary Showcase',
      date: 'May 10-11, 2025',
      venue: 'Western Washington University',
      description: 'Modern and contemporary works by emerging choreographers.',
      ticketLink: '#tickets'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      role: 'Parent',
      quote: 'Northwest Ballet Theater has been transformative for our daughter. The professional quality of their productions is remarkable.',
      image: '/api/placeholder/80/80'
    },
    {
      name: 'David Chen',
      role: 'Former Student',
      quote: 'The training I received here prepared me for my professional career. The faculty truly cares about each dancer\'s growth.',
      image: '/api/placeholder/80/80'
    },
    {
      name: 'Maria Rodriguez',
      role: 'Community Member',
      quote: 'NBT brings world-class ballet to our community. Their performances are always breathtaking and inspiring.',
      image: '/api/placeholder/80/80'
    }
  ];

  return (
    <main className="min-h-screen bg-ballet-light">
      {/* Hero Section */}
      <div className="bg-elegant-gradient text-white py-20">
        <div className="container-custom text-center">
          <h1 className="text-5xl lg:text-6xl font-elegant font-bold mb-6">
            Northwest Ballet Theater
          </h1>
          <p className="text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto mb-8">
            Professional ballet performances bringing artistry and excellence to the Pacific Northwest
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="btn-primary bg-white text-ballet-burgundy hover:bg-ballet-light">
              View Season Schedule
            </button>
            <button className="btn-outline border-white text-white hover:bg-white hover:text-ballet-burgundy">
              Purchase Tickets
            </button>
          </div>
        </div>
      </div>

      <div className="container-custom section-padding">
        {/* Navigation Tabs */}
        <div className="flex flex-wrap justify-center mb-12 bg-white rounded-2xl p-2 shadow-elegant">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'season', label: 'Current Season' },
            { id: 'company', label: 'Our Company' },
            { id: 'testimonials', label: 'Testimonials' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-ballet-burgundy text-white shadow-lg'
                  : 'text-ballet-burgundy hover:bg-ballet-light'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-16">
            {/* Theater Programs */}
            <section>
              <h2 className="text-4xl font-elegant font-bold gradient-text text-center mb-12">
                Our Theater Programs
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {theaterPrograms.map((program, index) => (
                  <div key={index} className="card-elevated group hover:shadow-2xl transition-all duration-300">
                    <div className="overflow-hidden rounded-t-2xl">
                      <img
                        src={program.image}
                        alt={program.title}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-elegant font-bold text-ballet-burgundy mb-3">
                        {program.title}
                      </h3>
                      <p className="text-gray-600 mb-4">{program.description}</p>
                      <a
                        href={program.link}
                        className="inline-flex items-center text-ballet-burgundy hover:text-ballet-gold font-semibold transition-colors duration-200"
                      >
                        Learn More
                        <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Mission Statement */}
            <section className="card-elevated">
              <div className="p-8 lg:p-12 text-center">
                <h2 className="text-3xl font-elegant font-bold gradient-text mb-6">
                  Our Mission
                </h2>
                <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
                  Northwest Ballet Theater is dedicated to presenting professional-quality ballet performances 
                  that inspire, educate, and enrich our community. We strive to provide opportunities for 
                  dancers of all levels to experience the joy and discipline of classical ballet while 
                  fostering an appreciation for this timeless art form throughout the Pacific Northwest.
                </p>
              </div>
            </section>
          </div>
        )}

        {/* Current Season Tab */}
        {activeTab === 'season' && (
          <div className="space-y-12">
            <div className="text-center">
              <h2 className="text-4xl font-elegant font-bold gradient-text mb-6">
                Season XVIII - 2024/2025
              </h2>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                Experience the magic of ballet with our carefully curated season featuring beloved classics
                and exciting contemporary works.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
              {upcomingShows.map((show, index) => (
                <div key={index} className="card-elevated">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-elegant font-bold text-ballet-burgundy">
                        {show.title}
                      </h3>
                      <span className="bg-ballet-light text-ballet-burgundy px-3 py-1 rounded-full text-sm font-semibold">
                        {show.date}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-2">
                      <strong>Venue:</strong> {show.venue}
                    </p>
                    <p className="text-gray-700 mb-6">{show.description}</p>
                    <a
                      href={show.ticketLink}
                      className="btn-primary w-full text-center"
                    >
                      Get Tickets
                    </a>
                  </div>
                </div>
              ))}
            </div>

            <div className="card-elevated">
              <div className="p-8 text-center">
                <h3 className="text-2xl font-elegant font-bold text-ballet-burgundy mb-4">
                  Season Subscriptions Available
                </h3>
                <p className="text-gray-700 mb-6">
                  Save up to 25% with a season subscription and enjoy priority seating,
                  exclusive events, and special perks.
                </p>
                <button className="btn-primary">
                  View Season
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Company Tab */}
        {activeTab === 'company' && (
          <div className="space-y-12">
            <div className="text-center">
              <h2 className="text-4xl font-elegant font-bold gradient-text mb-6">
                Meet Our Company
              </h2>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                Our talented company dancers bring passion, skill, and artistry to every performance,
                representing the finest in Pacific Northwest ballet.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Company Member Cards - These would be populated with actual dancer data */}
              {[1, 2, 3, 4, 5, 6].map((member) => (
                <div key={member} className="card-elevated text-center">
                  <div className="p-6">
                    <div className="w-32 h-32 bg-ballet-light rounded-full mx-auto mb-4 flex items-center justify-center">
                      <span className="text-ballet-burgundy text-4xl font-elegant">
                        {String.fromCharCode(64 + member)}
                      </span>
                    </div>
                    <h3 className="text-xl font-elegant font-bold text-ballet-burgundy mb-2">
                      Company Member {member}
                    </h3>
                    <p className="text-gray-600 mb-4">Principal Dancer</p>
                    <p className="text-sm text-gray-700">
                      Professional dancer with extensive training and performance experience.
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="card-elevated">
              <div className="p-8 text-center">
                <h3 className="text-2xl font-elegant font-bold text-ballet-burgundy mb-4">
                  Join Our Company
                </h3>
                <p className="text-gray-700 mb-6">
                  Interested in auditioning for Northwest Ballet Theater?
                  We welcome talented dancers to join our professional company.
                </p>
                <button className="btn-primary">
                  Audition Information
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Testimonials Tab */}
        {activeTab === 'testimonials' && (
          <div className="space-y-12">
            <div className="text-center">
              <h2 className="text-4xl font-elegant font-bold gradient-text mb-6">
                What People Say
              </h2>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                Hear from our community about their experiences with Northwest Ballet Theater.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="card-elevated">
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full mr-4"
                      />
                      <div>
                        <h4 className="font-semibold text-ballet-burgundy">{testimonial.name}</h4>
                        <p className="text-sm text-gray-600">{testimonial.role}</p>
                      </div>
                    </div>
                    <blockquote className="text-gray-700 italic">
                      "{testimonial.quote}"
                    </blockquote>
                  </div>
                </div>
              ))}
            </div>

            <div className="card-elevated">
              <div className="p-8 text-center">
                <h3 className="text-2xl font-elegant font-bold text-ballet-burgundy mb-4">
                  Share Your Experience
                </h3>
                <p className="text-gray-700 mb-6">
                  We'd love to hear about your experience with Northwest Ballet Theater.
                </p>
                <button className="btn-primary">
                  Submit Testimonial
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default Theater;
